import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { RouteMapPin } from '@/types/route-plan';
import { RouteMapPinBuilder, type RouteMapModel } from '@/lib/route-plan/RouteMapPinBuilder';
import { loadMapsModule } from '@/components/route-planner/map/MapsModule';
import { RouteMapPinStack } from '@/components/route-planner/map/RouteMapPinStack';
import { RoutePinCallout } from '@/components/route-planner/map/RoutePinCallout';
import { MapExpandButton } from '@/components/route-planner/map/MapExpandButton';
import { UnmappedStopsChip } from '@/components/route-planner/map/UnmappedStopsChip';

type RouteMapViewProps = {
  model: RouteMapModel;
  interactive: boolean;
  height?: number;                 // static banners; interactive fills flex 1
  onExpandPress?: () => void;      // renders the expand chip when provided
  showUnmappedChip?: boolean;      // full-screen map only
};

type PinStackGroup = { stackKey: string; pins: RouteMapPin[] };

/**
 * The one shared route map. Resolves react-native-maps lazily and renders
 * nothing when the module is unavailable (web, old binaries) or when there
 * are no pins. One Marker per shoulder stack; dotted polyline in step order.
 */
export function RouteMapView({
  model,
  interactive,
  height,
  onExpandPress,
  showUnmappedChip,
}: RouteMapViewProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const maps = loadMapsModule();

  const stacks = useMemo<PinStackGroup[]>(() => {
    const groups = new Map<string, RouteMapPin[]>();
    for (const pin of model.pins) {
      const group = groups.get(pin.stackKey);
      if (group) {
        group.push(pin);
      } else {
        groups.set(pin.stackKey, [pin]);
      }
    }
    return [...groups.entries()].map(([stackKey, pins]) => ({
      stackKey,
      pins: [...pins].sort((a, b) => a.stackOrder - b.stackOrder),
    }));
  }, [model.pins]);

  const region = useMemo(
    () =>
      RouteMapPinBuilder.regionFor(
        model.pins.map(({ latitude, longitude }) => ({ latitude, longitude })),
      ),
    [model.pins],
  );

  if (!maps || model.pins.length === 0 || !region) return null;

  const MapView = maps.default;
  const { Marker, Polyline, Callout } = maps;

  const map = (
    <MapView
      style={styles.map}
      {...(interactive ? { initialRegion: region } : { region })}
      scrollEnabled={interactive}
      zoomEnabled={interactive}
      rotateEnabled={interactive}
      pitchEnabled={interactive}
      toolbarEnabled={false}
      showsCompass={interactive}
    >
      <Polyline
        coordinates={model.polylineCoordinates}
        strokeWidth={2.5}
        strokeColor={palette.primary}
        lineDashPattern={[1, 6]}
      />
      {stacks.map((stack) => {
        const front = stack.pins[0];
        const hasCurrent = stack.pins.some((pin) => pin.state === 'current');
        const byStep = [...stack.pins].sort((a, b) => a.stepNumber - b.stepNumber);
        return (
          <Marker
            key={stack.stackKey}
            coordinate={{ latitude: front.latitude, longitude: front.longitude }}
            anchor={{ x: 0.5, y: 1 }}
            zIndex={hasCurrent ? 3 : 1}
            tracksViewChanges={hasCurrent || stack.pins.length > 1}
          >
            <RouteMapPinStack pins={stack.pins} />
            {interactive && (
              <Callout tooltip>
                <RoutePinCallout pins={byStep} />
              </Callout>
            )}
          </Marker>
        );
      })}
    </MapView>
  );

  if (interactive) {
    return (
      <View style={styles.fill}>
        {map}
        {showUnmappedChip && <UnmappedStopsChip stops={model.unmappedStops} />}
      </View>
    );
  }

  return (
    <View style={[styles.banner, { height: height ?? 120 }]}>
      <View style={styles.fill} pointerEvents="none">
        {map}
      </View>
      {onExpandPress && <MapExpandButton onPress={onExpandPress} />}
    </View>
  );
}

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
  map: {
    ...StyleSheet.absoluteFillObject,
  },
  banner: {
    borderRadius: 12,
    overflow: 'hidden',
  },
});
